import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  formatLogDataPretty,
  type DispatchStep,
  type DispatchLogEvent,
} from '../../types/dispatch';

interface StepDetailDrawerProps {
  open: boolean;
  step: DispatchStep | null;
  stepLogs: DispatchLogEvent[];
  runId: string;
  onClose: () => void;
  fetchStepLogs?: (runId: string, stepId: number) => Promise<unknown[]>;
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({
  children,
  value,
  index,
  contentSx,
}: TabPanelProps & { contentSx?: object }) {
  const visible = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!visible}
      style={{
        display: visible ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {visible && (
        <Box sx={{ py: 2, flex: 1, minHeight: 0, overflow: 'hidden', ...contentSx }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function formatTs(ts: string): string {
  try {
    const d = new Date(ts);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    return `${y}-${mo}-${day} ${h}:${m}:${s}.${ms}`;
  } catch {
    return ts;
  }
}

const LEVEL_COLORS: Record<string, string> = {
  INFO: '#6b7280',
  DEBUG: '#9ca3af',
  WARNING: '#f59e0b',
  ERROR: '#ef4444',
};

const StepDetailDrawer: React.FC<StepDetailDrawerProps> = ({
  open,
  step,
  stepLogs,
  onClose,
}) => {
  const [tab, setTab] = React.useState(0);

  if (!step) return null;

  const metrics = step.metrics || {};
  const error = step.error;
  const metricEntries = Object.entries(metrics).filter(
    ([, v]) => v !== undefined && v !== null
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 'min(720px, 55vw)' },
          height: '100vh',
          maxHeight: '100vh',
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">Step: {step.step_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Status: {step.status} • {step.duration_ms ?? '—'}ms
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Tabs
          value={tab}
          onChange={(_: React.SyntheticEvent, v: number) => setTab(v)}
          sx={{ mt: 2, flexShrink: 0 }}
        >
          <Tab label="Metrics" />
          <Tab label="Error" />
          <Tab label="Logs" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          {metricEntries.length === 0 ? (
            <Typography color="text.secondary">No metrics</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  {metricEntries.map(([key, val]) => (
                    <TableRow key={key}>
                      <TableCell component="th" sx={{ fontWeight: 500 }}>
                        {key}
                      </TableCell>
                      <TableCell>
                        {typeof val === 'object' ? (
                          <pre
                            style={{
                              margin: 0,
                              fontSize: '0.75rem',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {JSON.stringify(val)}
                          </pre>
                        ) : (
                          String(val)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tab} index={1}>
          {!error || Object.keys(error).length === 0 ? (
            <Typography color="text.secondary">No error</Typography>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                bgcolor: 'error.50',
                overflow: 'auto',
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(error, null, 2)}
              </pre>
            </Paper>
          )}
        </TabPanel>

        <TabPanel value={tab} index={2} contentSx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              bgcolor: '#1e1e1e',
              color: '#d4d4d4',
              p: 1,
              borderRadius: 1,
            }}
          >
            {stepLogs.length === 0 ? (
              <Typography color="gray">No logs for this step</Typography>
            ) : (
              stepLogs.map((log) => (
                <Box
                  key={log.id}
                  sx={{
                    py: 0.25,
                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                  }}
                >
                  <span style={{ color: '#6b7280' }}>[{formatTs(log.ts)}]</span>{' '}
                  <span
                    style={{
                      color: LEVEL_COLORS[log.level] || '#9ca3af',
                      fontWeight: log.level === 'ERROR' ? 600 : 400,
                    }}
                  >
                    {log.level}
                  </span>{' '}
                  {log.logger && (
                    <span style={{ color: '#60a5fa' }}>{log.logger}</span>
                  )}{' '}
                  {log.message}
                  {log.data != null && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 0.5,
                        ml: 1,
                        mb: 0,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'rgba(0,0,0,0.2)',
                        color: '#9ca3af',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflow: 'auto',
                        maxHeight: 280,
                      }}
                    >
                      {formatLogDataPretty(log.data)}
                    </Box>
                  )}
                  {log.exception && (
                    <Box component="details" sx={{ mt: 0.5, ml: 2 }}>
                      <summary style={{ color: '#f87171' }}>Exception</summary>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.75rem',
                        }}
                      >
                        {JSON.stringify(log.exception, null, 2)}
                      </pre>
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </TabPanel>
      </Box>
    </Drawer>
  );
};

export default StepDetailDrawer;
